import http from '../../../api/axios';
import { apiUrl } from '../../../api/apiUrl';
import { toast } from 'react-toastify';
import { handleApiErrors } from '../../../api/apiErrorHandler';
import {
  DirectionDoc,
  InvoiceBaseCreateRequest,
  InvoiceItem,
  DocumentType,
  DocumentStatus,
  DocumentOptionalField,
} from '../../../types/Invoice';
import { useNavigate } from 'react-router';
import { useCurrentCompany } from '../../../hooks/useCompany';
import { addDays } from 'date-fns/esm';
import { paths } from '../../RouterContainer';
import { useLoading } from '../../../context/loadingContext';
import { InvoiceFormContainer } from './InvoiceFormContainer';
import { filterObjectFromEmptyString } from '../../../utils/functions/data.functions';
import { useTranslation } from '../../../context/translationContext';
import { useBreadcrumbs } from '../../../hooks/useBreadcrumbs';
import { useEffect, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';

/**
 * Page component represents form with creating new invoice.
 */
export const InvoiceCreatePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { startLoading, stopLoading } = useLoading();
  const { langSetting } = useTranslation();
  const { company } = useCurrentCompany();
  const [optionalFields, setOptionalFields] = useState<DocumentOptionalField[] | null>(null);
  const [selectedOptionalFields, setSelectedOptionalFields] = useState<
    DocumentOptionalField[] | null
  >(null);

  const { setBreadcrumbs } = useBreadcrumbs();
  useEffect(() => {
    setBreadcrumbs([{ path: paths.NEW_INVOICE, name: 'navigation.new-document' }]);
    fetchOptionalFields();
  }, []);

  /**
   * Loads optional fields for current document
   */
  const fetchOptionalFields = async () => {
    if (!company) return;
    const req = `?parentId=${company.id}`;
    const response = await http.apiGet({
      url: `${apiUrl.OPTIONAL_FIELD_LIST}${req}`,
    });
    if (response?.data)
      setOptionalFields(response?.data.filter((x: DocumentOptionalField) => !!x.isActive));
  };

  useEffect(() => {
    if (!optionalFields) return;
    let _optionalFields: any[] = [];
    optionalFields.map((field: DocumentOptionalField) => {
      if (!field.isDefault) return;
      _optionalFields = [..._optionalFields, { optionalFieldId: field.id }];
    });
    setSelectedOptionalFields(_optionalFields);
  }, [optionalFields]);

  const initialInvoiceData: InvoiceBaseCreateRequest = {
    companyId: company?.id,
    conversionRate: 1,
    currencyRatesDate: undefined,
    dateOfTaxableTransaction: new Date(),
    paymentDate: new Date(),
    rounding: 0,
    direction: DirectionDoc.OUTPUT_DOC,
    documentStatus: DocumentStatus.CONCEPT,
    issued: new Date(),
    dueDate: addDays(new Date(), 14),
    items: [],
    total: 0,
    totalWithVat: 0,
    type: DocumentType.INVOICE,
    language: langSetting?.defaultLanguage,
  };
  /**
   * Handles create action for new invoice form submit.
   * Uses team create API.
   * @param values form data
   */
  const handleSubmit = async (data: InvoiceBaseCreateRequest) => {
    // delete temporary id from items (used to edit/delete before submit)
    const _invoiceItems =
      data.items &&
      data.items.map((item: InvoiceItem) => {
        return { ...item, id: undefined };
      });
    // filters data from values where are only empty strings
    const filteredData = filterObjectFromEmptyString(data);
    try {
      startLoading();
      let req = '';
      if (user?.fullName) req += `?fullName=` + user?.fullName;
      if (user?.phoneNumber) req += `&phoneNumber=` + user?.phoneNumber;
      if (company?.codeBookItemsUseCode)
        req += `&codeBookItemsUseCode=` + company?.codeBookItemsUseCode;
      if (company?.maxDecimalNumUnitPrice)
        req += `&maxDecimalNumUnitPrice=` + company?.maxDecimalNumUnitPrice;
      const response = await http.apiPost({
        url: `${apiUrl.ACCOUNTING_DOCUMENT_CREATE}${req}`,
        data: { ...filteredData, items: _invoiceItems, optionalFields: selectedOptionalFields },
      });
      if (response?.status === 201) {
        stopLoading();
        toast.success('Success!');
        navigate(`/${paths.INVOICE}/${response.data.id}`);
      }
    } catch (err) {
      stopLoading();
      handleApiErrors(err);
    }
  };

  return <InvoiceFormContainer data={initialInvoiceData} onSubmit={handleSubmit} />;
};
